import { createAction } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { localStorage } from 'utils'
import {
  AccountUserDisplayFlagType,
  UserDisplayFlagType,
} from 'apps/dashboard/constants/DisplayFlagType'
import { alert } from 'common/core'
import { AccountApi } from 'shared/api/requests/account'
import { accountUserApi } from 'shared/api/requests/accountUser'
import { profileApi } from 'shared/api/requests/profile'
import { FetchMessageTagAnalyticsResponse } from 'shared/api/requests/profile/schemas'
import { ProfileScopedApi } from 'shared/api/requests/profileScoped'
import { createAsyncAction } from 'shared/lib/redux'

import { Account } from '../models/Page/helpers'
import { getInactiveAccountIds } from '../selectors/profile'

export const sortPages = createAction<{
  sortFunction: (item: Account, item2: Account) => -1 | 0 | 1
  reset?: boolean
}>('profile/sortPages')

export const searchPages = createAction<string>('profile/searchPages')

export const setDisplayFlagLocaly = createAction<{
  displayFlag: UserDisplayFlagType
  value: boolean
}>('profile/setDisplayFlagLocaly')

export const setCurrentAccountUserDisplayFlagLocaly = createAction<{
  displayFlag: AccountUserDisplayFlagType
  value: boolean
}>('profile/setCurrentAccountUserDisplayFlagLocaly')

export const setUserMenuVisibility = createAction<boolean>('profile/setUserMenuVisibility')

/** Async thunks */

export const setSkipPreviewOnboardingModal = createAsyncAction(
  'profile/setSkipPreviewOnboardingModal',
  (status: boolean) =>
    profileApi.setSkipPreviewOnboardingModal({ body: { skip_preview_onboarding_modal: status } }),
)

export const sendEmailVerification = createAsyncAction(
  'profile/sendEmailVerification',
  async (email: string) => {
    const response = await profileApi.sendEmailVerification({ body: { email } })

    alert(l.translate('Verification email sent'), 'success')
    return response.data
  },
  {
    autoHandleByGuards: [
      profileApi.sendEmailVerification.isCommonEmailError,
      profileApi.sendEmailVerification.isEmailInvalidError,
      profileApi.sendEmailVerification.isEmailUsedError,
      profileApi.sendEmailVerification.isLimitExceededError,
    ],
  },
)

export const verifyEmailViaCode = createAsyncAction(
  'profile/verifyEmailViaCode',
  async (code: string) => {
    const response = await profileApi.verifyEmailViaCode({ body: { code } })

    alert(l.translate('Thank you for verification!'), 'success')
    return response.data
  },
  {
    autoHandleByGuards: [
      profileApi.verifyEmailViaCode.isEmailVerifiedError,
      profileApi.verifyEmailViaCode.isExpiredCodeError,
      profileApi.verifyEmailViaCode.isIncorrectCodeError,
      profileApi.verifyEmailViaCode.isLimitExceededError,
    ],
  },
)

export const confirmEmail = createAsyncAction('profile/confirmEmail', async (code?: string) => {
  if (code) {
    await profileApi.confirmEmail({ body: { code } })
  }
})

export const setProfileTimezone = createAsyncAction(
  'profile/setProfileTimezone',
  async (timezone: string) => {
    const response = await profileApi.setTimezone({ body: { timezone } })
    return response.data
  },
)

export const setDisplayFlag = createAsyncAction(
  'profile/setDisplayFlag',
  async ({ displayFlag, value }: { displayFlag: UserDisplayFlagType; value: boolean }) => {
    await profileApi.setDisplayFlag({
      body: { display_flag: displayFlag, value },
    })

    return { displayFlag, value }
  },
)

export const setCurrentAccountUserDisplayFlag = createAsyncAction(
  'profile/setCurrentAccountUserDisplayFlag',
  async ({ displayFlag, value }: { displayFlag: AccountUserDisplayFlagType; value: boolean }) => {
    await accountUserApi.setDisplayFlag({
      body: { display_flag: displayFlag, value },
    })
    return { displayFlag, value }
  },
)

export const fetchMessageTagAnalytics = createAsyncAction(
  'profile/fetchMessageTagAnalytics',
  async () => {
    let mockResponse: FetchMessageTagAnalyticsResponse | null = null
    try {
      mockResponse = JSON.parse(localStorage.getItem('health_flow_mock_data'))
    } catch (_) {}

    const response = await profileApi.fetchMessageTagAnalytics()
    const { last_available_date, analytics_date_from, accounts } = mockResponse || response.data

    return {
      lastAvailableDate: last_available_date,
      analyticsDateFrom: analytics_date_from,
      accounts,
    }
  },
)

export const fetchAllAccounts = createAsyncAction('profile/fetchAllAccounts', async () => {
  const response = await profileApi.fetchAllAccounts()
  return response.data.accounts
})

export const fetchAccount = createAsyncAction(
  'profile/fetchAccount',
  async (accountId: string) => {
    const response = await profileApi.fetchAccount({ query: { account_id: accountId } })
    return response.data.account
  },
  {
    condition: (accountId, { getState }) => {
      const account = getState().app.accounts.some((acc) => acc.id === accountId)
      return !account
    },
  },
)

export const fetchPages = createAsyncAction('profile/fetchPages', async (_, { getState }) => {
  const response = await profileApi.fetchPages()

  const state = getState()
  const inactiveAccountIds = getInactiveAccountIds(state)

  const pages = response.data.fb.accounts
  const defaultSortedPages = pages.sort((pageA, pageB) => {
    return (
      Number(inactiveAccountIds.includes(pageB.page_id)) -
      Number(inactiveAccountIds.includes(pageA.page_id))
    )
  })

  return defaultSortedPages
})

export const fetchInactiveAccounts = createAsyncAction(
  'profile/fetchInactiveAccounts',
  async () => {
    const response = await profileApi.fetchInactiveAccounts()
    return response.data.account_ids
  },
)

export const generateProfileApiToken = createAsyncAction(
  'profile/generateProfileApiToken',
  async () => {
    const response = await ProfileScopedApi.generate()
    return response.data
  },
)

export const deleteProfileApiToken = createAsyncAction('profile/deleteProfileApiToken', () =>
  ProfileScopedApi.delete(),
)

export const makeAccountFavorite = createAsyncAction(
  'profile/makeAccountFavorite',
  async ({ accountId, isFavorite }: { accountId: AccountId; isFavorite: boolean }) => {
    const response = await AccountApi.favorite({
      body: {
        account_id: accountId,
        is_favorite: isFavorite ? '1' : '0',
      },
    })
    return response.data
  },
  {
    autoHandleByGuards: [AccountApi.favorite.isSetFavoriteError],
  },
)

export const makeAccountHidden = createAsyncAction(
  'profile/makeAccountHidden',
  async ({ accountId, isHidden }: { accountId: AccountId; isHidden: boolean }) => {
    const response = await AccountApi.hide({
      body: {
        account_id: accountId,
        is_hidden: isHidden ? '1' : '0',
      },
    })

    return response.data
  },
  {
    autoHandleByGuards: [AccountApi.hide.isSetHiddenError],
  },
)
